import { BLACK, WHITE, hslToHex } from './symbolColors';
import type { TableFrameStyleId } from './layoutTypes';

export interface TableFrameStyleMeta {
  readonly id: TableFrameStyleId;
  readonly label: string;
  readonly headingBackground: string;
  readonly headingColor: string;
  readonly headingBorder: string;
  readonly headingUnderline: string;
  readonly rowBackground1: string;
  readonly rowBackground2: string;
  readonly rowBorder: string;
  readonly frameBorder: string;
  readonly boldHeading: boolean;
  readonly background1Count: number;
  readonly background2Count: number;
}

const BLUE = hslToHex(201, 72, 46);
const BLUE_LIGHT = hslToHex(201, 70, 90);
const GRAY = hslToHex(0, 0, 72);
const GRAY_LIGHT = hslToHex(0, 0, 92);
const ORANGE = hslToHex(28, 100, 50);
const ORANGE_LIGHT = hslToHex(28, 90, 92);
const TRANSPARENT = 'transparent';

export const TABLE_FRAME_STYLES: readonly TableFrameStyleMeta[] = [
  {
    id: 'grayDoubleAlternatingRows',
    label: 'Gray Double Alternating Rows',
    headingBackground: TRANSPARENT,
    headingColor: BLACK,
    headingBorder: TRANSPARENT,
    headingUnderline: GRAY,
    rowBackground1: GRAY_LIGHT,
    rowBackground2: WHITE,
    rowBorder: TRANSPARENT,
    frameBorder: TRANSPARENT,
    boldHeading: true,
    background1Count: 2,
    background2Count: 2,
  },
  {
    id: 'blueUnderlineAlternatingRows',
    label: 'Blue Underline Heading with Alternating Rows',
    headingBackground: TRANSPARENT,
    headingColor: BLACK,
    headingBorder: TRANSPARENT,
    headingUnderline: BLUE,
    rowBackground1: GRAY_LIGHT,
    rowBackground2: WHITE,
    rowBorder: TRANSPARENT,
    frameBorder: TRANSPARENT,
    boldHeading: true,
    background1Count: 1,
    background2Count: 1,
  },
  {
    id: 'blackBorderBoldHeading',
    label: 'Black Text and Border with Bold Heading',
    headingBackground: WHITE,
    headingColor: BLACK,
    headingBorder: BLACK,
    headingUnderline: BLACK,
    rowBackground1: WHITE,
    rowBackground2: WHITE,
    rowBorder: BLACK,
    frameBorder: BLACK,
    boldHeading: true,
    background1Count: 1,
    background2Count: 1,
  },
  {
    id: 'blueBackgroundWhiteBorder',
    label: 'Blue Background with White Border',
    headingBackground: BLUE,
    headingColor: WHITE,
    headingBorder: WHITE,
    headingUnderline: WHITE,
    rowBackground1: BLUE_LIGHT,
    rowBackground2: BLUE_LIGHT,
    rowBorder: WHITE,
    frameBorder: WHITE,
    boldHeading: true,
    background1Count: 1,
    background2Count: 1,
  },
  {
    id: 'blueAlternatingRows',
    label: 'Blue Alternating Rows',
    headingBackground: BLUE,
    headingColor: WHITE,
    headingBorder: BLUE,
    headingUnderline: BLUE,
    rowBackground1: BLUE_LIGHT,
    rowBackground2: WHITE,
    rowBorder: BLUE,
    frameBorder: BLUE,
    boldHeading: true,
    background1Count: 1,
    background2Count: 1,
  },
  {
    id: 'grayAlternatingRows',
    label: 'Gray Alternating Rows',
    headingBackground: GRAY,
    headingColor: BLACK,
    headingBorder: BLACK,
    headingUnderline: BLACK,
    rowBackground1: GRAY_LIGHT,
    rowBackground2: WHITE,
    rowBorder: BLACK,
    frameBorder: BLACK,
    boldHeading: true,
    background1Count: 1,
    background2Count: 1,
  },
  {
    id: 'orangeBackground',
    label: 'Orange Background with White Border',
    headingBackground: ORANGE,
    headingColor: WHITE,
    headingBorder: WHITE,
    headingUnderline: WHITE,
    rowBackground1: ORANGE_LIGHT,
    rowBackground2: ORANGE_LIGHT,
    rowBorder: ORANGE,
    frameBorder: ORANGE,
    boldHeading: true,
    background1Count: 1,
    background2Count: 1,
  },
  {
    id: 'orangeAlternatingRows',
    label: 'Orange Alternating Rows',
    headingBackground: ORANGE,
    headingColor: WHITE,
    headingBorder: BLACK,
    headingUnderline: BLACK,
    rowBackground1: WHITE,
    rowBackground2: ORANGE_LIGHT,
    rowBorder: BLACK,
    frameBorder: BLACK,
    boldHeading: true,
    background1Count: 1,
    background2Count: 1,
  },
  {
    id: 'grayBackground',
    label: 'Gray Background with Bold Heading',
    headingBackground: GRAY,
    headingColor: BLACK,
    headingBorder: GRAY,
    headingUnderline: GRAY,
    rowBackground1: WHITE,
    rowBackground2: WHITE,
    rowBorder: TRANSPARENT,
    frameBorder: TRANSPARENT,
    boldHeading: true,
    background1Count: 1,
    background2Count: 1,
  },
] as const;

export function tableFrameStyleMeta(styleId: TableFrameStyleId): TableFrameStyleMeta {
  return TABLE_FRAME_STYLES.find((style) => style.id === styleId)
    ?? TABLE_FRAME_STYLES[0];
}
